/* eslint-disable @typescript-eslint/no-var-requires */
import { ApolloProvider } from '@apollo/react-hooks';
import { GetTemporaryToken, _GetAccessToken } from '@netfront/gelada-react-shared';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { NextPageContext } from 'next';
import Head from 'next/head';
import fetch from 'node-fetch';
import React from 'react';

// example from https://github.com/zeit/next.js/blob/canary/examples/with-typescript-graphql

type TApolloClient = ApolloClient<NormalizedCacheObject>;

type InitialProps = {
  apolloClient: TApolloClient;
  apolloState: any;
} & Record<string, any>;

type WithApolloPageContext = {
  apolloClient: TApolloClient;
} & NextPageContext;

type WithApolloOptions = {
  ssr?: boolean;
  useTemporaryToken?: boolean;
};

let globalApolloClient: TApolloClient;

const apiLink = (useTemporaryToken: boolean): HttpLink => {
  const accessToken = _GetAccessToken();
  const temporaryToken = GetTemporaryToken();
  const authorization = useTemporaryToken ? temporaryToken : accessToken;

  return new HttpLink({
    fetch: fetch as any,
    headers: {
      authorization,
    },
    uri: process.env.REACT_APP_BONOBO_API_URL,
  });
};

const authLink = new HttpLink({
  fetch: fetch as any,
  headers: {
    custom_build_guid: process.env.REACT_APP_PROJECT_ID,
  },
  uri: process.env.REACT_APP_GELADA_API_URL,
});

/**
 * Creates and provides the apolloContext
 * to a next.js PageTree. Use it by wrapping
 * your PageComponent via HOC pattern.
 */
export default function withApollo(PageComponent: any, options: WithApolloOptions = {}) {
  const { ssr = true, useTemporaryToken = false } = options;

  const WithApollo = ({ apolloClient, apolloState, ...pageProps }: InitialProps) => {
    const client = apolloClient || initApolloClient(apolloState, useTemporaryToken);
    return (
      <ApolloProvider client={client}>
        <PageComponent {...pageProps} />
      </ApolloProvider>
    );
  };

  // Set the correct displayName in development
  if (process.env.NODE_ENV !== 'production') {
    const displayName = PageComponent.displayName || PageComponent.name || 'Component';

    if (displayName === 'App') {
      console.warn('This withApollo HOC only works with PageComponents.');
    }

    WithApollo.displayName = `withApollo(${displayName})`;
  }

  if (ssr || PageComponent.getInitialProps) {
    WithApollo.getInitialProps = async (ctx: WithApolloPageContext) => {
      const { AppTree } = ctx;

      // Initialize ApolloClient, add it to the ctx object so
      // we can use it in `PageComponent.getInitialProp`.
      const apolloClient = (ctx.apolloClient = initApolloClient({}, useTemporaryToken));

      // Run wrapped getInitialProps methods
      let pageProps = {};
      if (PageComponent.getInitialProps) {
        pageProps = await PageComponent.getInitialProps(ctx);
      }

      // Only on the server:
      if (typeof window === 'undefined') {
        // When redirecting, the response is finished.
        // No point in continuing to render
        if (ctx.res && ctx.res.finished) {
          return pageProps;
        }

        // Only if ssr is enabled
        if (ssr) {
          try {
            // Run all GraphQL queries
            const { getDataFromTree } = await import('@apollo/react-ssr');
            await getDataFromTree(
              <AppTree
                pageProps={{
                  ...pageProps,
                  apolloClient,
                }}
              />,
            );
          } catch (error) {
            // Prevent Apollo Client GraphQL errors from crashing SSR.
            // Handle them in components via the data.error prop:
            // https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-query-data-error
            console.error('Error while running `getDataFromTree`', error);
          }

          // getDataFromTree does not call componentWillUnmount
          // head side effect therefore need to be cleared manually
          // Head.rewind();
        }
      }

      // Extract query data from the Apollo store
      const apolloState = apolloClient.cache.extract();

      return {
        ...pageProps,
        apolloState,
      };
    };
  }

  return WithApollo;
}

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 * @param  {Object} initialState
 */
function initApolloClient(initialState?: any, useTemporaryToken = false) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, useTemporaryToken);
  }

  // Reuse client on the client-side
  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState, useTemporaryToken);
  }

  return globalApolloClient;
}

/**
 * Creates and configures the ApolloClient
 * @param  {Object} [initialState={}]
 */
function createApolloClient(initialState = {}, useTemporaryToken = false) {
  const ssrMode = typeof window === 'undefined';
  const cache = new InMemoryCache().restore(initialState);

  // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
  return new ApolloClient({
    ssrMode,
    link: ApolloLink.split(
      (operation) => operation.getContext().clientName === 'auth', // Routes the query to the proper client
      authLink,
      apiLink(useTemporaryToken),
    ),
    cache,
  });
}
